/**
 * Vue 앱 진입점 — Pinia + Vue Router + Tanstack Query 등록.
 */
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { VueQueryPlugin } from '@tanstack/vue-query';
import App from './App.vue';
import { router } from './router';
import { queryClient } from './api/query-client';
import { setUnauthorizedHandler } from './api/http';
import { useAuthStore } from './stores/auth';
import { useDashboardSummary } from './components/dashboard/useDashboardSummary';
import './styles/main.css';

// 화면 조각(js/css)을 못 받으면 앱이 그리다 멈춰 흰 화면이 된다. 배포 중에 받은 오류 응답이
// 브라우저 캐시에 남아 있으면 새로고침해도 같은 오류를 다시 쓴다(2026-11-04 Payments).
// 그 파일을 캐시 무시로 다시 받아 캐시를 덮고, 한 번만 다시 연다(무한 새로고침 방지).
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'preload-retry';
  try {
    if (sessionStorage.getItem(KEY)) return;
    sessionStorage.setItem(KEY, '1');
  } catch {
    return;
  }
  event.preventDefault();
  const url = String((event as Event & { payload?: unknown }).payload ?? '').match(/\/assets\/[^\s'"]+/)?.[0];
  void (url ? fetch(url, { cache: 'reload' }).catch(() => undefined) : Promise.resolve()).then(() => location.reload());
});
window.addEventListener('load', () => {
  // 정상으로 다 뜬 뒤에는 표시를 지운다 — 다음 배포 때 다시 한 번 복구할 수 있게.
  setTimeout(() => { try { sessionStorage.removeItem('preload-retry'); } catch { /* 무시 */ } }, 5000);
});

const app = createApp(App);
app.use(createPinia());
app.use(router);
app.use(VueQueryPlugin, { queryClient });

// 세션이 끊기면 로그인 화면으로 보낸다. 토스트로 알리고 화면을 그대로 두면
// 사용자는 아무것도 안 되는 화면에서 토스트만 닫게 된다.
//
// 돌아올 곳을 redirect 로 넘겨 로그인 후 하던 자리로 되돌린다. 이미 로그인
// 화면이면 아무것도 하지 않는다 — 스스로에게 다시 이동하면 입력 중이던
// 것이 날아간다.
setUnauthorizedHandler(() => {
  useAuthStore().$reset();
  // 대시보드의 공유 상태도 비운다 — 안 그러면 다시 로그인해도 'Login required.'
  // 가 화면에 남는다(SPA 라 페이지가 새로 뜨지 않는다).
  useDashboardSummary().resetForNewSession();
  const current = router.currentRoute.value;
  if (current.name === 'login') return;
  void router.replace({ name: 'login', query: { redirect: current.fullPath } });
});

app.mount('#app');
